import type { Core, GooglePay, ResultCode, SubmitActions } from '@adyen/adyen-web';
import { createAdyenGooglePayConfiguration, getGooglePayComponentOptions } from '@/utils/adyen';
import type { Customer } from '@/types/checkout';
import { getLocale } from '@/utils/get-locale-util';
import type { AdyenGooglePayConfig, CreateGooglePayComponentParams } from '@/types/adyen';
import { Errors } from '@/constants/errors';

class AdyenExpressService {
  private ExpressCheckoutInstance: Core | null = null;
  private isInitializing = false;
  private googlePayActions: SubmitActions | null = null;

  setGooglePayActions(actions: SubmitActions): void {
    this.googlePayActions = actions;
  }

  completeGooglePayPayment(success: boolean, resultCode?: ResultCode): void {
    if (this.googlePayActions) {
      if (success && resultCode) {
        this.googlePayActions.resolve({ resultCode });
      } else {
        this.googlePayActions.reject();
      }
      this.googlePayActions = null;
    }
  }

  async initializeCheckoutInstance(
    config: AdyenGooglePayConfig,
    customer?: Customer,
    paymentMethodConfig?: Record<string, unknown>
  ): Promise<Core> {
    if (this.ExpressCheckoutInstance) {
      return this.ExpressCheckoutInstance;
    }

    if (this.isInitializing) {
      return this.initializeCheckoutInstance(config, customer, paymentMethodConfig);
    }

    this.isInitializing = true;

    try {
      const configuration = createAdyenGooglePayConfiguration(config, customer, paymentMethodConfig);
      const { pspLocale } = getLocale();
      const fullConfig = { ...configuration, locale: pspLocale };

      if (!fullConfig.environment) {
        throw new Error('Adyen environment is required (test or live)');
      }

      const adyenWeb = await import('@adyen/adyen-web');
      const AdyenCheckoutClass = adyenWeb.AdyenCheckout || (adyenWeb as Record<string, unknown>).default;

      if (!AdyenCheckoutClass || typeof AdyenCheckoutClass !== 'function') {
        throw new Error(Errors.payment.failedToImportAdyenCheckout);
      }

      const instance = await AdyenCheckoutClass(fullConfig);
      this.ExpressCheckoutInstance = instance;
      return this.ExpressCheckoutInstance;
    } finally {
      this.isInitializing = false;
    }
  }

  async createGooglePayComponent(params: CreateGooglePayComponentParams): Promise<GooglePay | null> {
    if (!this.ExpressCheckoutInstance) return null;

    const options = getGooglePayComponentOptions(params);

    try {
      const { GooglePay } = await import('@adyen/adyen-web');

      const component = new GooglePay(this.ExpressCheckoutInstance, {
        ...options,
      } as never);
      return component;
    } catch (error) {
      console.error('Failed to create Google Pay component:', error);
      return null;
    }
  }

  resetGooglePay(): void {
    this.ExpressCheckoutInstance = null;
    this.isInitializing = false;
    this.googlePayActions = null;
  }
}

export const adyenExpressService = new AdyenExpressService();
